import React from 'react';

import _ from 'lodash';

import {
    queryListBillingProfiles,
    queryCreateSubscription,
    queryListSubscriptions,
} from '../../../Queries';

import ProcessingIndicator from '../../Common/ProcessingIndicator';
import ActionButton from '../../Common/ActionButton';
import NoticeBlock from '../../Common/NoticeBlock';
import MessageList from '../../Common/MessageList';

import Field from '../../Fields/Field';
import InputTextField from '../../Fields/InputTextField';
import InputSelectField from '../../Fields/InputSelectField';
import InputRadioGroupField from '../../Fields/InputRadioGroupField';
import InputPayorPanel from '../../Fields/InputPayorPanel';

import BillingProfile from '../../../Entities/BillingProfile';
import Payor from '../../../Entities/Payor';
import Subscription from '../../../Entities/Subscription';

import './SubscriptionDetailNewSubscriptionPanel.scss';

import {
    validateSubscriptionName,
    sanitizeRemoveSpaceFromStart,
    sanitizeRemoveSpaceFromEnd,
    sanitizeRemoveDoubleSpaces
} from '../../../Validators/Subscription';

import featureFlags from '../../../featureFlags';


const billingProfileQueryFields = [
    {
        'billing_profiles': [
            'id',
            'name',
            'validation_only',
        ]
    }
];

const subscriptionQueryFields = [
    'id'
];


class SubscriptionDetailNewSubscriptionPanel extends React.Component {
    state = {
        panelInitializationComplete: false,
        panelInitializationErrors: [],
        subscriptionBillingProfileId: null,
        subscription: new Subscription(),
        payor: new Payor(),
        payorPassword: null,
        createSubscriptionTaskInProgress: false,
        createSubscriptionTaskErrors: [],
        showConfirmationPopUp: false,
    };

    fieldConfiguration = {
        billingProfile: {},
        subscription: {},
        payor: {}
    };


    async componentDidMount() {
        const initializationTasks = [];

        initializationTasks.push(async () => {
            const { facilityId } = this.props;
            const billingProfiles = await this.fetchBillingProfiles(facilityId);

            const billingProfileOptions = billingProfiles.map(billingProfile => {
                return {
                    value: billingProfile.fields.id.value,
                    label: billingProfile.fields.name.value
                };
            });

            this.fieldConfiguration.billingProfile = {
                billingProfiles: {
                    options: billingProfileOptions
                }
            };
        });

        initializationTasks.push(async () => {
            const values = await Subscription.getFieldAllowedValues([
                'mail_invoices',
                'tax_exempt',
                'status'
            ]);

            this.fieldConfiguration.subscription = values;
        });

        initializationTasks.push(async () => {
            const values = await Payor.getFieldAllowedValues([
                'status',
                'type',
                'billing_state',
                'info_state'
            ]);

            this.fieldConfiguration.payor = values;
        });

        try {
            await Promise.all(initializationTasks.map(task => task()));

            const { payor } = this.state;
            payor.fields['status'].value = 'ACTIVE';
            this.setState({
                panelInitializationComplete: true,
                payor
            });
        } catch (errors) {
            this.setState({
                panelInitializationComplete: true,
                panelInitializationErrors: errors
            });
        }
    }


    fetchBillingProfiles(facilityId) {
        return new Promise((resolve, reject) => {
            queryListBillingProfiles({
                filters: [{
                    model: 'BILLING_PROFILE',
                    field: 'facility_id',
                    op: 'EQ',
                    value: facilityId
                }, {
                    model: 'BILLING_PROFILE',
                    field: 'active',
                    op: 'EQ',
                    value: true
                }]
            }, billingProfileQueryFields).then(response => {
                if (response.errors) {
                    reject(response.errors.map(error => error.message));
                    return;
                }

                const billingProfiles = response.data.listBillingProfiles.billing_profiles.map(billingProfile =>
                    BillingProfile.createFromQueryValues(billingProfile)
                );

                resolve(billingProfiles);
            }, error => {
                reject([error]);
            });
        });
    }


    createSubscription() {
        const {
            subscriptionBillingProfileId,
            subscription,
            payor,
            payorPassword
        } = this.state;

        this.setState({
            createSubscriptionTaskInProgress: true,
            createSubscriptionTaskErrors: []
        }, () => {
            const variables = {
                billing_profile_id: subscriptionBillingProfileId,
                new_payor_password: payorPassword,
                payor: payor.getQueryFieldValues(),
                subscription: subscription.getQueryFieldValues()
            };

            if (featureFlags.SUBSCRIPTION_NAME) {
                //must be done as part of save/fe validation phase and not input change - so user can type space
                const sanitizedName = sanitizeRemoveSpaceFromEnd(subscription.fields.subscription_name.value);
                subscription.fields.subscription_name.value = sanitizedName;
                this.setState(subscription);

                try {
                    validateSubscriptionName(subscription.fields.subscription_name.value);
                } catch (error) {
                    return this.setState({
                        createSubscriptionTaskInProgress: false,
                        createSubscriptionTaskErrors: error
                    });
                }
            }

            queryCreateSubscription(variables, subscriptionQueryFields).then(response => {
                const createSubscriptionTaskErrors = [];
                let subscription = null;

                if (response.errors) {
                    response.errors.forEach(error => {
                        createSubscriptionTaskErrors.push(error.message);
                    });
                }
                else {
                    subscription = Subscription.createFromQueryValues(response.data.createSubscription);
                }

                this.setState({
                    createSubscriptionTaskInProgress: false,
                    createSubscriptionTaskErrors
                }, () => {
                    if (subscription) {
                        this.props.setSubscriptionId(subscription.fields.id.value);
                    }
                });
            });
        });
    }

    checkSubscriptions() {
        const {
            subscriptionBillingProfileId,
            payor,
        } = this.state;

        const filters = [
            {
                model: 'PAYOR',
                field: 'billing_email',
                op: 'EQ',
                value: payor.fields.billing_email.value,
            },
            {
                model: 'BILLING_PROFILE',
                field: 'validation_only',
                op: 'EQ',
                value: false
            },
            {
                model: 'BILLING_PROFILE',
                field: 'id',
                op: 'EQ',
                value: subscriptionBillingProfileId,
            }
        ];
        const subscriptionsQueryFields = [
            'total',
        ];
        const variables = { filters };

        queryListSubscriptions(variables, subscriptionsQueryFields).then(response => {
            if (response.data.listSubscriptions.total > 0) {
                this.setState({ showConfirmationPopUp: true })
            } else {
                this.createSubscription();
            }
        });

    }

    onSubscriptionBillingProfileIdInputChange = value => {
        this.setState({
            subscriptionBillingProfileId: value
        });
    }

    _onSubscriptionInputChange(inputId) {
        return value => {
            switch (inputId) {
                case 'subscription_name':
                    if (value) {
                        value = sanitizeRemoveSpaceFromStart(value);
                        value = sanitizeRemoveDoubleSpaces(value);
                    }
                    break;
                case 'mail_invoices':
                case 'tax_exempt':
                    value = (value === 'true') ? true : (value === 'false') ? false : null;
                    break;
                default:
            }

            const subscription = _.cloneDeep(this.state.subscription);
            subscription.fields[inputId].value = value;

            this.setState({
                subscription
            });
        };
    }
    onPayorInputChange = value => {
        this.setState({
            payor: value
        });
    }
    onPayorPasswordInputChange = value => {
        this.setState({
            payorPassword: value
        });
    }
    onSubscriptionInvoiceNotesChange = subscriptionInvoiceNotes => {
        let subscriptionToEdit = _.cloneDeep(this.state.subscription);
        subscriptionToEdit.fields.subscription_invoice_notes.value = subscriptionInvoiceNotes;
        this.setState({ subscription: subscriptionToEdit });
    }

    onSubscriptionNumParkingSpacesChange = numberOfParkingSpaces => {
        const subscriptionToEdit = _.cloneDeep(this.state.subscription);
        subscriptionToEdit.fields.numberOfParkingSpaces.value = numberOfParkingSpaces;
        this.setState({ subscription: subscriptionToEdit });
    }

    onCreateSubscriptionClick = () => {
        this.createSubscription();
    }

    onSaveClick = () => {
        this.checkSubscriptions();
    }

    onCloseClick = () => {
        this.setState({ showConfirmationPopUp: false })
    }

    renderPanelInitializationBlock() {
        const {
            panelInitializationComplete,
            panelInitializationErrors
        } = this.state;

        return (
            <React.Fragment>
                {(!panelInitializationComplete) &&
                    <div className="parkchirp--page--user-message-container">
                        <ProcessingIndicator userMessage="Initializing, please wait..." />
                    </div>
                }
                {(panelInitializationComplete && panelInitializationErrors.length > 0) &&
                    <div className="parkchirp--page--user-message-container">
                        <div className="parkchirp--page--user-message">
                            <p>The following errors occurred while initializing the new Subscription panel:</p>
                            <ul>
                                {panelInitializationErrors.map(error =>
                                    <li key={performance.now()}>{error}</li>
                                )}
                            </ul>
                        </div>
                    </div>
                }
            </React.Fragment>
        );
    }


    renderPanelMainContent() {
        return (
            <div className="parkchirp--grid">
                <div className="parkchirp--grid-column parkchirp--grid-column-size--main-content">
                    {this.renderNewSubscriptionForm()}
                </div>
                <div className="parkchirp--grid-column parkchirp--grid-column-size--sidebar">
                    {this.renderSidebar()}
                </div>
            </div>
        )
    }


    renderNewSubscriptionForm() {
        const {
            subscriptionBillingProfileId,
            subscription,
            payor,
            payorPassword
        } = this.state;

        return (
            <React.Fragment>
                <div className="parkchirp--fieldgroup" style={{ borderWidth: featureFlags.ACCOUNT_ID ? "0px" : "2px" }}>
                    <div className="parkchirp--fieldgroup-content">
                        <div style={{ display: "flex", flexDirection: "row" }}>
                            {featureFlags.SUBSCRIPTION_NAME &&

                                <div style={{ width: "50%" }}>
                                    <Field label="Subscription Name"
                                        id="parkchirp--subscription--sub-name"
                                        layout="columns">
                                        <InputTextField inputId="parkchirp--subscription--sub-name"
                                            value={subscription.fields.subscription_name.value}
                                            onChange={this._onSubscriptionInputChange('subscription_name')}
                                            editMode={true}
                                        />
                                    </Field>
                                </div>
                            }
                            {featureFlags.ACCOUNT_ID &&
                                <div style={{ width: "50%" }}>
                                    <div class="parkchirp--field-container parkchirp--field-container--columns">
                                        <label class="parkchirp--field-label parkchirp--field-label--sm">Account ID</label>
                                        <div class="parkchirp--field-value">
                                            <div class="parkchirp--input-text-field">Auto-generated when subscription is saved</div>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                        <Field label="Billing Profile"
                            id="parkchirp--subscription--billing-profile-id"
                            layout="columns">
                            <InputSelectField inputId="parkchirp--subscription--billing-profile-id"
                                options={[
                                    { value: null, label: '' },
                                    ...this.fieldConfiguration.billingProfile.billingProfiles.options
                                ]}
                                value={subscriptionBillingProfileId}
                                onChange={this.onSubscriptionBillingProfileIdInputChange}
                                editMode={true}
                            />
                        </Field>
                        <Field label="Mail Invoices"
                            id="parkchirp--subscription--mail-invoices"
                            layout="columns">
                            <InputRadioGroupField inputId="parkchirp--subscription--mail-invoices"
                                options={this.fieldConfiguration.subscription.mail_invoices.options}
                                value={subscription.fields.mail_invoices.value}
                                onChange={this._onSubscriptionInputChange('mail_invoices')}
                                editMode={true}
                            />
                        </Field>
                        <Field label="Tax Exempt"
                            id="parkchirp--subscription--tax-exempt"
                            layout="columns">
                            <InputRadioGroupField inputId="parkchirp--subscription--tax-exempt"
                                options={this.fieldConfiguration.subscription.tax_exempt.options}
                                value={subscription.fields.tax_exempt.value}
                                onChange={this._onSubscriptionInputChange('tax_exempt')}
                                editMode={true}
                            />
                        </Field>
                    </div>
                </div>
                {!featureFlags.ACCOUNT_ID && <h2 className="parkchirp--fieldgroup-heading">Payor</h2>}
                <InputPayorPanel payor={payor}
                    payorPassword={payorPassword}
                    subscription={subscription}
                    subscriptionInvoiceNotes={subscription.fields.subscription_invoice_notes.value}
                    onPayorInputChange={this.onPayorInputChange}
                    onPayorPasswordInputChange={this.onPayorPasswordInputChange}
                    onSubscriptionInvoiceNotesChange={this.onSubscriptionInvoiceNotesChange}
                    onSubscriptionNumParkingSpacesChange={this.onSubscriptionNumParkingSpacesChange}
                    fieldConfiguration={this.fieldConfiguration}
                    newMode={true}
                />
            </React.Fragment>
        );
    }


    renderSidebar() {
        const {
            createSubscriptionTaskInProgress,
            createSubscriptionTaskErrors,
            showConfirmationPopUp,
            payor
        } = this.state;

        return (
            <>
                <div className="parkchirp--sidebar-block"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: '20px'
                    }}>
                    <ActionButton label="Save"
                        inProgressState={createSubscriptionTaskInProgress}
                        onClick={this.onSaveClick}
                    />
                </div>

                {(createSubscriptionTaskErrors.length > 0) &&
                    <NoticeBlock type="error"
                        containerClasses={['parkchirp--sidebar-block']}>
                        <MessageList heading={`The following errors occurred while attempting to create the Subscription:`}
                            messages={createSubscriptionTaskErrors}
                        />
                    </NoticeBlock>
                }

                {showConfirmationPopUp &&
                    <NoticeBlock type="error"
                        containerClasses={['parkchirp--sidebar-block']}>
                        <p>A subscription already exists for {payor.fields.billing_email.value} on this billing profile. Are you sure you want to continue?</p>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            paddingTop: '20px'
                        }}>
                            <ActionButton
                                label="Cancel"
                                onClick={this.onCloseClick}
                            />
                            <ActionButton
                                label="Continue"
                                inProgressState={createSubscriptionTaskInProgress}
                                onClick={this.onCreateSubscriptionClick}

                            />
                        </div>
                    </NoticeBlock>
                }
            </>
        );
    }


    render() {
        const {
            panelInitializationComplete,
            panelInitializationErrors
        } = this.state;

        return (
            <React.Fragment>
                {(!panelInitializationComplete || panelInitializationErrors.length > 0) &&
                    this.renderPanelInitializationBlock()
                }
                {(panelInitializationComplete && panelInitializationErrors.length === 0) &&
                    this.renderPanelMainContent()
                }
            </React.Fragment>
        );
    }
}
export default SubscriptionDetailNewSubscriptionPanel;
